import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";

const TEST_VISUAL = {
  rapido: {
    icon: "fa-bolt-lightning",
    accent: "from-emerald-500 to-emerald-700",
    border: "border-emerald-200",
    badge: "bg-emerald-50 text-emerald-700",
    label: "Triagem",
  },
  copsoq: {
    icon: "fa-magnifying-glass-chart",
    accent: "from-indigo-500 to-indigo-700",
    border: "border-indigo-200",
    badge: "bg-indigo-50 text-indigo-700",
    label: "Aprofundado",
  },
  hse: {
    icon: "fa-shield-halved",
    accent: "from-amber-500 to-orange-600",
    border: "border-amber-200",
    badge: "bg-amber-50 text-amber-700",
    label: "Internacional",
  },
};

export default function TestCenter() {
  const [tests, setTests] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([api.get("/psy/tests"), api.get("/psy/campaigns")])
      .then(([t, c]) => {
        setTests(t.data.tests || []);
        setCampaigns(c.data || []);
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Erro ao carregar"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-8" data-testid="psy-test-center">
      {/* Header */}
      <header className="space-y-2">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 text-white shadow-lg">
            <i className="fa-solid fa-brain text-xl"></i>
          </span>
          <div>
            <h1 className="text-3xl font-bold text-slate-900" data-testid="psy-title">
              Central de Testes Psicossociais NR-01
            </h1>
            <p className="text-slate-500">
              Aplique avaliações validadas, gere matriz de risco e plano de ação automático.
            </p>
          </div>
        </div>
      </header>

      {/* Test cards */}
      <section>
        <h2 className="text-lg font-semibold text-slate-800 mb-4">
          <i className="fa-solid fa-folder-tree mr-2 text-slate-400"></i>
          Escolha o tipo de teste para iniciar uma nova campanha
        </h2>
        {loading ? (
          <div className="text-slate-400">Carregando catálogo…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {tests.map((t) => {
              const v = TEST_VISUAL[t.id] || TEST_VISUAL.rapido;
              return (
                <div
                  key={t.id}
                  className={`relative overflow-hidden rounded-2xl border ${v.border} bg-white shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col`}
                  data-testid={`test-card-${t.id}`}
                >
                  <div className={`h-2 bg-gradient-to-r ${v.accent}`}></div>
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-start justify-between mb-3">
                      <span className={`inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${v.accent} text-white shadow-md`}>
                        <i className={`fa-solid ${v.icon} text-xl`}></i>
                      </span>
                      <span className={`text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full ${v.badge}`}>
                        {v.label}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 leading-snug">{t.name}</h3>
                    <p className="text-sm text-slate-600 mt-2 flex-1">{t.description}</p>

                    <dl className="mt-5 space-y-2 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-slate-500"><i className="fa-regular fa-clock mr-1.5"></i>Tempo médio</dt>
                        <dd className="font-medium text-slate-800">{t.time_estimate}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-slate-500"><i className="fa-solid fa-list-check mr-1.5"></i>Questões</dt>
                        <dd className="font-medium text-slate-800">{t.question_count}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-slate-500"><i className="fa-solid fa-diagram-project mr-1.5"></i>Dimensões</dt>
                        <dd className="font-medium text-slate-800">{t.dimensions_count}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-slate-500"><i className="fa-solid fa-layer-group mr-1.5"></i>Profundidade</dt>
                        <dd className="font-medium text-slate-800">{t.profundidade}</dd>
                      </div>
                    </dl>

                    <p className="mt-4 text-xs text-slate-500 italic">
                      <i className="fa-solid fa-circle-info mr-1"></i>{t.indicado_para}
                    </p>

                    <button
                      type="button"
                      onClick={() => navigate(`/app/testes-psicossociais/nova?test=${t.id}`)}
                      className={`mt-6 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r ${v.accent} text-white font-semibold py-3 hover:shadow-lg transition-shadow`}
                      data-testid={`btn-create-${t.id}`}
                    >
                      <i className="fa-solid fa-plus"></i>
                      Criar campanha
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Análise consolidada por empresa */}
      {!loading && campaigns.length > 0 && (() => {
        const byCompany = {};
        campaigns.forEach((c) => {
          const cid = c.company_id;
          if (!cid) return;
          if (!byCompany[cid]) {
            byCompany[cid] = {
              company_id: cid,
              razao_social:
                c.company_snapshot?.razao_social ||
                c.company_snapshot?.nome_fantasia ||
                "Empresa sem nome",
              cnpj: c.company_snapshot?.cnpj || "",
              campaign_count: 0,
              response_count: 0,
              test_ids: new Set(),
            };
          }
          byCompany[cid].campaign_count += 1;
          byCompany[cid].response_count += (c.response_count || 0);
          byCompany[cid].test_ids.add(c.test_id);
        });
        const items = Object.values(byCompany).sort((a, b) =>
          a.razao_social.localeCompare(b.razao_social)
        );
        if (items.length === 0) return null;
        return (
          <section className="bg-white rounded-2xl shadow-sm border border-slate-200" data-testid="psy-companies-aggregate-section">
            <header className="px-6 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
              <div>
                <h2 className="text-lg font-semibold text-slate-800">
                  <i className="fa-solid fa-layer-group mr-2 text-emerald-500"></i>
                  Análise consolidada por empresa
                </h2>
                <p className="text-xs text-slate-500">
                  Unifique todas as avaliações psicossociais de uma empresa em uma única
                  visão executiva nas 9 dimensões NR-01.
                </p>
              </div>
              <span className="text-xs font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 px-2 py-1 rounded-full">
                {items.length} empresa(s)
              </span>
            </header>
            <ul className="divide-y divide-slate-100" data-testid="psy-companies-aggregate-list">
              {items.map((it) => (
                <li
                  key={it.company_id}
                  className="px-6 py-4 hover:bg-emerald-50/30 transition flex items-center gap-4"
                  data-testid={`psy-company-row-${it.company_id}`}
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-700 text-white shrink-0">
                    <i className="fa-solid fa-building"></i>
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{it.razao_social}</div>
                    <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                      {it.cnpj && <span><i className="fa-solid fa-id-card mr-1"></i>{it.cnpj}</span>}
                      <span><i className="fa-solid fa-folder mr-1"></i>{it.campaign_count} campanha(s)</span>
                      <span><i className="fa-solid fa-users mr-1"></i>{it.response_count} resp.</span>
                      <span><i className="fa-solid fa-toolbox mr-1"></i>{it.test_ids.size} instrumento(s)</span>
                    </div>
                  </div>
                  <Link
                    to={`/app/testes-psicossociais/empresa/${it.company_id}`}
                    className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-emerald-600 to-emerald-700 text-white text-xs font-semibold px-3 py-2 hover:shadow-md"
                    data-testid={`btn-company-aggregate-${it.company_id}`}
                  >
                    <i className="fa-solid fa-chart-pie"></i>
                    Análise consolidada
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        );
      })()}

      {/* Campanhas list */}
      <section className="bg-white rounded-2xl shadow-sm border border-slate-200">
        <header className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">
              <i className="fa-solid fa-folder-open mr-2 text-slate-400"></i>
              Campanhas
            </h2>
            <p className="text-xs text-slate-500">{campaigns.length} campanha(s) criada(s)</p>
          </div>
        </header>
        {campaigns.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">
            <i className="fa-regular fa-folder-closed text-3xl block mb-3"></i>
            Nenhuma campanha ainda. Escolha um teste acima para começar.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="psy-campaigns-list">
            {campaigns.map((c) => {
              const v = TEST_VISUAL[c.test_id] || TEST_VISUAL.rapido;
              return (
                <li key={c.id} className="px-6 py-4 hover:bg-slate-50 transition flex items-center gap-4">
                  <span className={`inline-flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br ${v.accent} text-white shrink-0`}>
                    <i className={`fa-solid ${v.icon}`}></i>
                  </span>
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/app/testes-psicossociais/${c.id}`}
                      className="font-semibold text-slate-900 hover:text-emerald-700 truncate block"
                      data-testid={`campaign-link-${c.id}`}
                    >
                      {c.titulo}
                    </Link>
                    <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                      <span><i className="fa-solid fa-tag mr-1"></i>{c.test_name}</span>
                      {c.setor_ghe && <span><i className="fa-solid fa-sitemap mr-1"></i>{c.setor_ghe}</span>}
                      <span><i className="fa-solid fa-users mr-1"></i>{c.response_count} resp.</span>
                      <span className="capitalize"><i className="fa-solid fa-circle-dot mr-1"></i>{c.status}</span>
                    </div>
                  </div>
                  <Link
                    to={`/app/testes-psicossociais/${c.id}`}
                    className="text-emerald-700 hover:text-emerald-900 text-sm font-medium"
                  >
                    Abrir
                    <i className="fa-solid fa-arrow-right ml-1"></i>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
